// IntroTravel
// Features:
// - Manages the initial camera movement sequence (Fly-in)
// - Controls overlay and logo visibility during the transition
// - Automatically transitions to MenuIdle phase upon completion

import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import gsap from 'gsap'

import { usePhase, PHASES } from '../../store/usePhase'
import { useActions } from '../../store/useActions'

const INTRO_CONFIG = {
    startPos: new THREE.Vector3(12, 25, 18),
    startTarget: new THREE.Vector3(-6.893, 14, 2.757),
    menuPos: new THREE.Vector3(9.131, 2.46, 14.6277),
    menuTarget: new THREE.Vector3(-6.893, 6.093, 2.757),
    duration: 4.0,
    ease: 'power2.out',
}

export default function IntroTravel({ controlsRef }) {
    const camera = useThree((state) => state.camera)
    const phase = usePhase((state) => state.phase)
    const setPhase = usePhase((state) => state.setPhase)

    const showLogo = useActions((state) => state.showLogo)
    const hideOverlay = useActions((state) => state.hideOverlay)

    const startedRef = useRef(false)

    useEffect(() => {
        if (!camera || phase !== PHASES.introTravel || startedRef.current) {
            if (phase !== PHASES.introTravel) startedRef.current = false
            return
        }

        startedRef.current = true

        // 1. Reset to start pose
        camera.position.copy(INTRO_CONFIG.startPos)
        if (controlsRef?.current) {
            controlsRef.current.target.copy(INTRO_CONFIG.startTarget)
            controlsRef.current.update()
        }

        // 2. Animate Camera Position
        gsap.to(camera.position, {
            x: INTRO_CONFIG.menuPos.x,
            y: INTRO_CONFIG.menuPos.y,
            z: INTRO_CONFIG.menuPos.z,
            duration: INTRO_CONFIG.duration,
            ease: INTRO_CONFIG.ease,
            onStart: hideOverlay,
            onComplete: () => {
                showLogo()
                setPhase(PHASES.menuIdle)
                startedRef.current = false
            },
        })

        // 3. Animate Controls Target
        if (controlsRef?.current) {
            gsap.to(controlsRef.current.target, {
                x: INTRO_CONFIG.menuTarget.x,
                y: INTRO_CONFIG.menuTarget.y,
                z: INTRO_CONFIG.menuTarget.z,
                duration: INTRO_CONFIG.duration,
                ease: INTRO_CONFIG.ease,
                onUpdate: () => controlsRef.current.update(),
            })
        }
    }, [phase, camera, controlsRef, setPhase, showLogo, hideOverlay])

    return null
}
